import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  AppConfig,
  ClientVersionCeiling,
  UpdateAppConfigInput,
} from "../types";

const APP_CONFIG_FIELDS = `
  id
  trashRetentionDays
  accountDeletionGraceDays
  emailServiceEnabled
  androidMinVersionCode
  iosMinVersionCode
  webappMinVersionCode
  androidLatestVersionCode
  iosLatestVersionCode
  webappLatestVersionCode
  updateMessage
  createdAt
  updatedAt
`;

export const appConfigApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAppConfig: builder.query<AppConfig, void>({
      query: () => ({
        body: {
          query: `
            query AppConfig {
              appConfig {
                ${APP_CONFIG_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<AppConfig>(res, "appConfig"),

      providesTags: [{ type: "AppConfig", id: "SINGLETON" }],
    }),

    // `force` bypasses the server's lock-out guard on the minimum version
    // fields. Only send it when the operator has been shown what it will do
    // and confirmed — see settings/app-config.tsx.
    updateAppConfig: builder.mutation<
      AppConfig,
      UpdateAppConfigInput & { force?: boolean }
    >({
      query: ({ force, ...input }) => ({
        body: {
          query: `
            mutation UpdateAppConfig($input: UpdateAppConfigInput!, $force: Boolean) {
              updateAppConfig(input: $input, force: $force) {
                ${APP_CONFIG_FIELDS}
              }
            }
          `,
          variables: { input, force: force ?? false },
        },
      }),

      transformResponse: (res: unknown) => pick<AppConfig>(res, "updateAppConfig"),

      invalidatesTags: [{ type: "AppConfig", id: "SINGLETON" }],
    }),

    getClientVersionCeilings: builder.query<ClientVersionCeiling[], void>({
      query: () => ({
        body: {
          query: `
            query ClientVersionCeilings {
              clientVersionCeilings {
                platform
                safeVersionCode
                userCount
                highestSeenVersionCode
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) =>
        pick<ClientVersionCeiling[]>(res, "clientVersionCeilings"),
    }),
  }),
});

export const {
  useGetAppConfigQuery,
  useLazyGetAppConfigQuery,
  useUpdateAppConfigMutation,
  useGetClientVersionCeilingsQuery,
} = appConfigApi;
